import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Header from './components/common/Header';
import Footer from './components/common/Footer';
import BottomNav from './components/common/BottomNav';
import { FAB } from './components/common/FAB';
import ProtectedRoute from './components/common/ProtectedRoute';
import RestrictedRoute from './components/common/RestrictedRoute';
import HomePage from './pages/HomePage';
import SchedulerPage from './pages/SchedulerPage';
import LottoPage from './pages/LottoPage';
import LottoHistoryPage from './pages/LottoHistoryPage';
import SwapPage from './pages/SwapPage';
import SwipeTestPage from './pages/SwipeTestPage';
import AdminSchedulePage from './pages/AdminSchedulePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import { AuthProvider } from './contexts/AuthContext';
import { ScheduleProvider } from './contexts/ScheduleContext';
import { LottoProvider } from './contexts/LottoContext';
import { SwapProvider } from './contexts/SwapContext';
import { ThemeProvider } from './contexts/ThemeContext';
import './App.css';

function App() {
  return (
    <Router>
      <AuthProvider>
        <ThemeProvider>
          <AppContent />
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
}

function AppContent() {
  const location = useLocation();
  const isAuthPage = ['/login', '/register'].includes(location.pathname);
  const isPublicPage = ['/privacy', '/terms'].includes(location.pathname);

  // 로그인 페이지는 다른 Provider 없이
  if (isAuthPage) {
    return (
      <div className="min-h-screen bg-base-dawn flex flex-col">
        <Header />
        
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
        </Routes>
        <Toaster
          position="top-right"
          reverseOrder={false}
          gutter={8}
          toastOptions={{
            duration: 3000,
            style: {
              background: 'rgb(var(--color-bg-surface))',
              color: 'rgb(var(--color-text-primary))',
              padding: '16px',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              fontSize: '14px',
              fontWeight: '500',
            },
            success: {
              duration: 3000,
              style: {
                background: '#10B981',
                color: '#fff',
              },
              iconTheme: {
                primary: '#fff',
                secondary: '#10B981',
              },
            },
            error: {
              duration: 4000,
              style: {
                background: '#EF4444',
                color: '#fff',
              },
              iconTheme: {
                primary: '#fff',
                secondary: '#EF4444',
              },
            },
            loading: {
              style: {
                background: '#D96B3F',
                color: '#fff',
              },
            },
          }}
        />
      </div>
    );
  }

  // 프라이버시 정책 등 공개 페이지 (인증 불필요, 헤더/네비 없음)
  if (isPublicPage) {
    return (
      <Routes>
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
      </Routes>
    );
  }

  // 인증된 페이지는 모든 Provider 포함
  return (
    <ScheduleProvider>
      <LottoProvider>
        <SwapProvider>
          <div className="min-h-screen bg-base-dawn flex flex-col overflow-x-hidden">
            <Header />
            
            <div className="flex-1 pb-4">
              <Routes>
                <Route 
                  path="/" 
                  element={
                    <ProtectedRoute>
                      <HomePage />
                    </ProtectedRoute>
                  } 
                />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route 
                  path="/scheduler" 
                  element={
                    <ProtectedRoute>
                      <SchedulerPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/lotto" 
                  element={
                    <RestrictedRoute>
                      <LottoPage />
                    </RestrictedRoute>
                  } 
                />
                <Route 
                  path="/lotto/history" 
                  element={
                    <RestrictedRoute>
                      <LottoHistoryPage />
                    </RestrictedRoute>
                  } 
                />
                <Route 
                  path="/swap" 
                  element={
                    <RestrictedRoute>
                      <SwapPage />
                    </RestrictedRoute>
                  } 
                />
                <Route 
                  path="/swipe-test" 
                  element={
                    <RestrictedRoute>
                      <SwipeTestPage />
                    </RestrictedRoute>
                  } 
                />
                <Route 
                  path="/profile" 
                  element={
                    <RestrictedRoute>
                      <ProfilePage />
                    </RestrictedRoute>
                  } 
                />
                <Route 
                  path="/admin" 
                  element={
                    <RestrictedRoute>
                      <AdminPage />
                    </RestrictedRoute>
                  } 
                />
                <Route 
                  path="/admin/schedule" 
                  element={
                    <RestrictedRoute>
                      <AdminSchedulePage />
                    </RestrictedRoute>
                  } 
                />
              </Routes>
            </div>

            {/* 하단 네비게이션 바 (인증 페이지 제외) */}
            <BottomNav />
            
            {/* Footer (인증 페이지 제외) */}
            {!isAuthPage && <Footer />}
                    
            {/* Toast 알림 */}
            <Toaster
              position="top-right"
              reverseOrder={false}
              gutter={8}
              toastOptions={{
                // 기본 설정
                duration: 3000,
                style: {
                  background: '#fff',
                  color: '#2A1B3D',
                  padding: '16px',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  fontSize: '14px',
                  fontWeight: '500',
                },
                // 성공 알림
                success: {
                  duration: 3000,
                  style: {
                    background: '#10B981',
                    color: '#fff',
                  },
                  iconTheme: {
                    primary: '#fff',
                    secondary: '#10B981',
                  },
                },
                // 에러 알림
                error: {
                  duration: 4000,
                  style: {
                    background: '#EF4444',
                    color: '#fff',
                  },
                  iconTheme: {
                    primary: '#fff',
                    secondary: '#EF4444',
                  },
                },
                // 로딩 알림
                loading: {
                  style: {
                    background: '#D96B3F',
                    color: '#fff',
                  },
                },
              }}
            />
            
            {/* FAB (로그인 페이지 제외) */}
            {!isAuthPage && <FAB />}
          </div>
        </SwapProvider>
      </LottoProvider>
    </ScheduleProvider>
  );
}

export default App;
// Rebuild 1773902347
